import React, { useState, useEffect, useCallback } from 'react';
import '../../../styles/components/management/steps/Forms.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const safeJson = (str, fallback) => {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export default function TaskForm({ unitId, stepId, initial = {}, onClose, onSaved }) {
  const isNew = !initial.TASK_TEMPLATE_ID;

  const [form, setForm] = useState({
    name: '',
    desc: '',
    kind: 'text-answer',
    mediaURL: '',
    uploadedFile: null,
    exampleText: '',
    options: ['', ''],
    inventoryItems: [{ name: '', type: 'quantity', value: '' }]
  });

  useEffect(() => {
    if (isNew) return;

    const { TASK_NAME, DESCRIPTION, TASK_KIND, EXAMPLE_MEDIA } = initial;
    const initForm = {
      name: TASK_NAME || '',
      desc: DESCRIPTION || '',
      kind: TASK_KIND || 'text-answer',
      mediaURL: EXAMPLE_MEDIA || '',
    };

    fetch(`${API_BASE}/app/API/rbmgt/task_details.php?task_id=${initial.TASK_TEMPLATE_ID}`)
      .then(r => r.json())
      .then(({ success, details }) => {
        if (!success || !details) return;
        setForm(prev => ({
          ...prev,
          ...initForm,
          exampleText: details.EXAMPLE_TEXT || '',
          options: safeJson(details.OPTIONS, ['', '']),
          inventoryItems: safeJson(details.INVENTORY_ITEMS, [{ name: '', type: 'quantity', value: '' }])
        }));
      })
      .catch(err => {
        console.error('❌ Failed to fetch task details', err);
      });
  }, [initial, isNew]);

  const updateForm = useCallback((field, value) => {
    setForm(f => ({ ...f, [field]: value }));
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("⚠️ File too large (max 10MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      updateForm('uploadedFile', { data: reader.result, type: file.type });
      updateForm('mediaURL', '');
    };
    reader.readAsDataURL(file);
  }, [updateForm]);

  const updateInvField = useCallback((i, field, value) => {
    const newItems = [...form.inventoryItems];
    newItems[i] = { ...newItems[i], [field]: value };
    updateForm('inventoryItems', newItems);
  }, [form.inventoryItems, updateForm]);

  const renderPreview = () => {
    const file = form.uploadedFile;
    if (!file) return null;
    return file.type.startsWith('image/') ? (
      <img src={file.data} alt="Preview" className="media-preview" />
    ) : file.type.startsWith('video/') ? (
      <video controls className="media-preview">
        <source src={file.data} type={file.type} />
      </video>
    ) : null;
  };

  const fetchWithDebug = async (url, options = {}, label = 'API call') => {
    try {
      console.debug(`📡 ${label}`, url, options);
      const res = await fetch(url, options);
      const json = await res.json();
      console.debug(`✅ ${label} response`, json);
      return json;
    } catch (err) {
      console.error(`❌ ${label} failed`, err);
      return { success: false, message: err.message };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stepId) return alert('⚠️ No step selected');

    let taskId = initial.TASK_TEMPLATE_ID;
    const tplPayload = {
      step_template_id: stepId,
      name: form.name.trim(),
      description: form.desc.trim(),
      kind: form.kind
    };

    const tplRes = await fetchWithDebug(
      `${API_BASE}/app/API/rbmgt/task_template.php`,
      {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(isNew ? tplPayload : { id: taskId, ...tplPayload })
      },
      'Save Task Template'
    );

    if (!tplRes.success) return alert(`Template error: ${tplRes.message}`);
    taskId = tplRes.id || taskId;

    if (isNew) {
      const linkRes = await fetchWithDebug(
        `${API_BASE}/app/API/rbmgt/unit_step_task.php`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unit_id: unitId,
            step_template_id: stepId,
            task_template_id: taskId
          })
        },
        'Link Task to Unit+Step'
      );

      if (!linkRes.success) return alert(`Link error: ${linkRes.message}`);
    }

    const detailsPayload = {
      task_template_id: taskId,
      example_media: form.uploadedFile?.data || form.mediaURL.trim(),
      example_text: form.exampleText.trim().replace(/<\/?[^>]+(>|$)/g, ""),
      options: form.options.filter(Boolean),
      inventory_items: form.inventoryItems.filter(i => i.name.trim())
    };

    const detRes = await fetchWithDebug(
      `${API_BASE}/app/API/rbmgt/task_details.php`,
      {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(detailsPayload)
      },
      'Save Task Details'
    );

    if (!detRes.success) return alert(`Details error: ${detRes.message}`);

    onSaved();
    onClose();
  };

  return (
    <div className="modal">
      <h3>{isNew ? 'Add Task' : 'Edit Task'}</h3>
      <form onSubmit={handleSubmit}>
        <label>Kind</label>
        <select value={form.kind} onChange={e => updateForm('kind', e.target.value)}>
          <option value="text-answer">Text Answer</option>
          <option value="single-answer">Single Answer</option>
          <option value="multiple-choice">Multiple Choice</option>
          <option value="photo-upload">Photo Upload</option>
          <option value="inventory-check">Inventory Check</option>
        </select>

        <label>Name</label>
        <input value={form.name} onChange={e => updateForm('name', e.target.value)} required />

        <label>Description</label>
        <textarea value={form.desc} onChange={e => updateForm('desc', e.target.value)} required />

        <label>Upload Image/Video</label>
        <input type="file" accept="image/*,video/*" onChange={handleFileChange} />

        <label>or Media URL</label>
        <input type="url" value={form.mediaURL} onChange={e => updateForm('mediaURL', e.target.value)} />

        {renderPreview()}

        {form.kind === 'text-answer' && (
          <>
            <label>Example Answer</label>
            <input value={form.exampleText} onChange={e => updateForm('exampleText', e.target.value)} />
          </>
        )}

        {(form.kind === 'single-answer' || form.kind === 'multiple-choice') && (
          <fieldset>
            <legend>Options</legend>
            {form.options.map((opt, i) => (
              <div key={i} className="option-row">
                <input value={opt} onChange={e => {
                  const opts = [...form.options];
                  opts[i] = e.target.value;
                  updateForm('options', opts);
                }} />
                <button type="button" aria-label="Remove option" onClick={() => updateForm('options', form.options.filter((_, idx) => idx !== i))}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => updateForm('options', [...form.options, ''])}>+ Add Option</button>
          </fieldset>
        )}

        {form.kind === 'inventory-check' && (
          <fieldset>
            <legend>Inventory Items</legend>
            {form.inventoryItems.map((it, i) => (
              <div key={i} className="inv-row">
                <input placeholder="Name" value={it.name} onChange={e => updateInvField(i, 'name', e.target.value)} />
                <select value={it.type} onChange={e => updateInvField(i, 'type', e.target.value)}>
                  <option value="quantity">Quantity</option>
                  <option value="liters">Liters</option>
                  <option value="kg">Kg</option>
                </select>
                <input placeholder="Value" value={it.value} onChange={e => updateInvField(i, 'value', e.target.value)} />
                <button type="button" aria-label="Remove inventory item" onClick={() => updateForm('inventoryItems', form.inventoryItems.filter((_, idx) => idx !== i))}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => updateForm('inventoryItems', [...form.inventoryItems, { name: '', type: 'quantity', value: '' }])}>+ Add Item</button>
          </fieldset>
        )}

        <div className="modal-buttons">
          <button type="submit">{isNew ? 'Create' : 'Save'}</button>
          <button type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
